import axios from "axios";
import axiosInstance from "utils/request";
import { BACKEND_DOMAIN, CDN_DOMAIN } from "config";
import { type ObjectType } from "store/appData";
import {
  DatasetSchema,
  DatasetDetailSchema,
  DatasetImageSchema,
  DefectDatasetSchema,
  DefectDatasetDetailSchema,
  type Dataset,
  type DatasetDetail,
  type DatasetImage,
  type DefectDataset,
  type DefectDatasetDetail,
  ImageAnnotationSchema,
  type ImageAnnotation,
  UnsuperverifyResultSchema,
  CropListResponseSchema,
} from "./schemas";
import { z } from "zod";

// `workflow_status` table ids — global reference data, identical in every
// region. Mirrors apps/global-api's `WorkflowStatus` enum.
export const TRUE_POSITIVE = 2;
export const SUPERVISOR_APPROVED = 3;
export const FALSE_POSITIVE = 4;
export const SUPER_FALSE_POSITIVE = 5;

export type {
  Dataset,
  DatasetDetail,
  DatasetImage,
  DefectDataset,
  DefectDatasetDetail,
  ImageAnnotation,
};

export type DatasetKind = "object" | "defect";

// `_low_0_0` is the always-present low-res tile; SpeedImage builds the rest.
export function cdnImageUrl(image: DatasetImage): string {
  const { baseUrl, extension } = cdnImageBase(image);
  return extension ? `${baseUrl}_low_0_0.${extension}` : `${baseUrl}_low_0_0`;
}

export function cdnImageBase(image: DatasetImage): {
  baseUrl: string;
  extension: string;
} {
  const dot = image.src.lastIndexOf(".");
  const extension = dot >= 0 ? image.src.slice(dot + 1) : "";
  const stem = dot >= 0 ? image.src.slice(0, dot) : image.src;
  // Encode per segment so characters like `#` in the S3 key are escaped
  // (the object key contains a literal `#`) while keeping `/` as separators.
  const encodedStem = stem.split("/").map(encodeURIComponent).join("/");
  return {
    baseUrl: `${CDN_DOMAIN}/${image.region}/image/${image.project_id}${encodedStem}`,
    extension,
  };
}

export function cdnDatasetImageUrl(
  region: string,
  datasetId: number,
  path: string,
): string {
  return `${CDN_DOMAIN}/${region}/dataset_image/${datasetId}/${path}`;
}

export type CropListResponse = z.infer<typeof CropListResponseSchema>;

export async function listDatasetImageCrops(
  region: string,
  datasetId: number,
  imageId: number,
  kind: DatasetKind,
  signal?: AbortSignal,
): Promise<string[]> {
  const response = await axios.get<string[]>(
    `${CDN_DOMAIN}/${region}/dataset_image/list/${datasetId}/${imageId}`,
    {
      params: { defect: kind === "defect" },
      signal,
      withCredentials: true,
    },
  );
  return CropListResponseSchema.parse(response.data);
}

async function getDatasets(): Promise<Dataset[]> {
  const response = await axiosInstance.get("/datasets");
  return DatasetSchema.array().parse(response.data);
}

async function getDataset(datasetId: number): Promise<DatasetDetail> {
  const response = await axiosInstance.get(`/datasets/${datasetId}`);
  return DatasetDetailSchema.parse(response.data);
}

export async function getImageAnnotations(
  region: string,
  projectId: number,
  imageId: number,
  signal?: AbortSignal,
): Promise<ImageAnnotation[]> {
  const response = await axiosInstance.get(
    `/${region}/${projectId}/${imageId}/annotations`,
    { signal },
  );
  return ImageAnnotationSchema.array().parse(response.data);
}
async function unsuperverifyObjectDatasetImage(
  region: string,
  projectId: number,
  datasetId: number,
  imageId: number,
): Promise<number> {
  const response = await axiosInstance.post(
    `/${region}/${projectId}/datasets/${datasetId}/images/${imageId}/unsuperverify`,
  );
  return UnsuperverifyResultSchema.parse(response.data).updated_count;
}

async function unsuperverifyDefectDatasetImage(
  region: string,
  projectId: number,
  datasetId: number,
  imageId: number,
): Promise<number> {
  const response = await axiosInstance.post(
    `/${region}/${projectId}/defect-datasets/${datasetId}/images/${imageId}/unsuperverify`,
  );
  return UnsuperverifyResultSchema.parse(response.data).updated_count;
}

async function getDefectDatasets(): Promise<DefectDataset[]> {
  const response = await axiosInstance.get("/defect-datasets");
  return DefectDatasetSchema.array().parse(response.data);
}

async function getDefectDataset(
  datasetId: number,
): Promise<DefectDatasetDetail> {
  const response = await axiosInstance.get(`/defect-datasets/${datasetId}`);
  return DefectDatasetDetailSchema.parse(response.data);
}

type ImageCallback = (image: DatasetImage) => void;

async function streamImages(
  url: string,
  onImage: ImageCallback,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(url, { credentials: "include", signal });
  if (!response.ok || !response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.trim()) continue;
      onImage(DatasetImageSchema.parse(JSON.parse(line)));
    }
  }
}

async function streamDatasetImages(
  datasetId: number,
  region: string,
  onImage: ImageCallback,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${BACKEND_DOMAIN}/datasets/${datasetId}/images/stream?region=${encodeURIComponent(region)}`;
  await streamImages(url, onImage, signal);
}

async function streamDefectDatasetImages(
  datasetId: number,
  region: string,
  onImage: ImageCallback,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${BACKEND_DOMAIN}/defect-datasets/${datasetId}/images/stream?region=${encodeURIComponent(region)}`;
  await streamImages(url, onImage, signal);
}

// Defect datasets colour boxes by defect status rather than object type.
// False positive takes priority even if the box also carries an issue type.
export type DefectStatus = "falsePositive" | "issue" | "clean";

export const DEFECT_BOX_COLOR: Record<DefectStatus, string> = {
  falsePositive: "#0072B2",
  issue: "#FF0000",
  clean: "#32CD32",
};

function getDefectStatus(
  annotation: ImageAnnotation,
  objectTypes: Record<number, ObjectType>,
): DefectStatus {
  const isFalsePositive = annotation.types.some(
    (type) =>
      type.workflow_status === FALSE_POSITIVE ||
      type.workflow_status === SUPER_FALSE_POSITIVE,
  );
  if (isFalsePositive) return "falsePositive";

  const hasIssue = annotation.types.some(
    (type) => objectTypes[type.object_type_id]?.issue,
  );
  return hasIssue ? "issue" : "clean";
}

interface DatasetApiEntry {
  list: () => Promise<Dataset[] | DefectDataset[]>;
  detail: (id: number) => Promise<DatasetDetail | DefectDatasetDetail>;
  streamImages: (
    id: number,
    region: string,
    onImage: ImageCallback,
    signal?: AbortSignal,
  ) => Promise<void>;
  unsuperverify: (
    region: string,
    projectId: number,
    datasetId: number,
    imageId: number,
  ) => Promise<number>;
  basePath: `/${string}`;
  // Returns true when the image counts as a "positive" for filter purposes.
  // Object datasets: any SUPERVISOR_APPROVED pair suffices.
  // Defect datasets: the pair must also be a defect type (issue=true).
  hasPositive: (
    image: DatasetImage,
    objectTypes: Record<number, ObjectType>,
  ) => boolean;
  // Resolves the colour an annotation box should be rendered with. Object
  // datasets colour by object type; defect datasets colour by defect status
  // instead, and also report the status key for styling/testing.
  getAnnotationColor: (
    annotation: ImageAnnotation,
    objectTypes: Record<number, ObjectType>,
  ) => { color: string; defectStatus?: DefectStatus };
}

export const DATASET_API: Record<DatasetKind, DatasetApiEntry> = {
  object: {
    list: getDatasets,
    detail: getDataset,
    streamImages: streamDatasetImages,
    unsuperverify: unsuperverifyObjectDatasetImage,
    basePath: "/datasets",
    hasPositive: (image) =>
      image.type_x_workflow_status.some(
        ([, status]) => status === SUPERVISOR_APPROVED,
      ),
    getAnnotationColor: (annotation, objectTypes) => ({
      color: objectTypes[annotation.types[0].object_type_id].color,
    }),
  },
  defect: {
    list: getDefectDatasets,
    detail: getDefectDataset,
    streamImages: streamDefectDatasetImages,
    unsuperverify: unsuperverifyDefectDatasetImage,
    basePath: "/datasets/defect",
    hasPositive: (image, objectTypes) =>
      image.type_x_workflow_status.some(
        ([typeId, status]) =>
          status === SUPERVISOR_APPROVED && objectTypes[typeId]?.issue === true,
      ),
    getAnnotationColor: (annotation, objectTypes) => {
      const defectStatus = getDefectStatus(annotation, objectTypes);
      return { color: DEFECT_BOX_COLOR[defectStatus], defectStatus };
    },
  },
};
